import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { Button } from '../ui/Button';
import { PageLoader } from '../ui/PageLoader';
import { PageHeader } from '../ui/PageHeader';
import PageBackLink from '../ui/PageBackLink';
import { BadgeCreator } from '../badges/BadgeCreator';
import { EntityPhotoUpload } from '../EntityPhotoUpload';
import OwnerVehicleCombobox, { type OwnerVehicleOption } from './OwnerVehicleCombobox';
import CommunityPostFeed from '../community/CommunityPostFeed';
import type { CommunityPost } from '../../types/communityPosts';
import { useOnboarding } from '../../hooks/useOnboarding';
import { RoleOnboardingCard } from '../onboarding/OnboardingCards';
import { shouldShowClubGuide } from '../../utils/onboarding';
import { clubVerificationLabel, clubIsPubliclyListed } from '../../utils/clubStatus';
import SubjectPassportQrPanel from '../passport/SubjectPassportQrPanel';

const ClubManagePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const onboarding = useOnboarding();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<OwnerVehicleOption | null>(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [showCreator, setShowCreator] = useState(false);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      setData(await apiService.getClub(slug));
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  const linkedVehicleIds = useMemo(() => {
    const rows = (data?.vehicles || []) as Array<{ vehicle_id?: number; id?: number }>;
    return rows
      .map((v) => Number(v.vehicle_id || v.id || 0))
      .filter((id) => id > 0);
  }, [data]);

  if (loading) return <PageLoader message="Loading club management" />;
  if (!data?.can_manage) {
    return (
      <div className="page max-w-3xl mx-auto px-4 py-12">
        <PageBackLink label="Back to Clubs" to="/clubs" className="mb-4" />
        <p className="kicker mb-2">Club</p>
        <h1 className="heading-section mb-3">Access denied</h1>
        <p className="text-muted" role="alert">{error || 'You do not have permission to manage this club.'}</p>
        {slug ? (
          <Link to={`/clubs/${slug}`} className="text-orange-400 hover:text-orange-300 mt-3 inline-block min-h-[44px]">
            View club profile
          </Link>
        ) : null}
      </div>
    );
  }

  const club = data.club;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">
        <PageBackLink label={`Back to ${club.name}`} to={`/clubs/${club.slug}`} className="mb-2" />
        <PageHeader
          className="mt-2 mb-0"
          compact
          eyebrow="Club"
          title="Club settings"
          description={`Status: ${clubVerificationLabel(club.verification_status)}`}
        />

        {shouldShowClubGuide(onboarding.state) ? (
          <RoleOnboardingCard
            title="Running this club"
            body="Complete the profile, review join requests, link builds, post updates, and issue recognition when it is earned."
            onDismiss={() => void onboarding.dismissClubGuide()}
          />
        ) : null}

          {club.verification_status === 'draft' ? (
            <Button
              className="mt-3"
              variant="secondary"
              onClick={async () => {
                await apiService.submitClub(club.id);
                await load();
              }}
            >
              Send for review
            </Button>
          ) : null}
          {clubIsPubliclyListed(club.verification_status) ? (
            <p className="text-sm text-gray-400 mt-3">
              This club is listed on the public directory
              {club.verification_status === 'unverified' ? ' without a verified badge' : ''}.
            </p>
          ) : null}

        <CommunityPostFeed
          scope="club"
          entityId={Number(club.id)}
          canView
          canPost
          initialItems={(data.updates?.items || []) as CommunityPost[]}
          initialTotal={Number(data.updates?.total || 0)}
          emptyMemberHint="Post meetups and announcements your members will see on the club page and home feed."
        />

        <section className="rounded-xl border border-white/[0.06] bg-[#0c0c0c]/50 p-4 sm:p-5">
          <EntityPhotoUpload
            label="Club profile photo"
            helpText="Shown on the club page and directory. JPEG, PNG, or WebP up to 5MB."
            shape="circle"
            currentUrl={club.logo_path}
            onUpload={async (file) => {
              const result = await apiService.uploadClubLogo(club.id, file);
              return result.logo_path;
            }}
            onDelete={async () => {
              await apiService.deleteClubLogo(club.id);
            }}
            onUpdated={(url) => {
              setData((prev: any) =>
                prev
                  ? {
                      ...prev,
                      club: { ...prev.club, logo_path: url },
                    }
                  : prev
              );
            }}
            onError={(msg) => setError(msg)}
          />
        </section>

        <SubjectPassportQrPanel
          subjectType="club"
          subjectId={Number(club.id)}
          subjectLabel={club.name}
          heading="Passport QR"
        />

        <section>
          <h2 className="heading-section mb-3">Pending members</h2>
          <ul className="space-y-2">
            {(data.members || [])
              .filter((m: any) => m.status === 'pending')
              .map((m: any) => (
                <li key={m.id} className="flex items-center justify-between gap-3 rounded-lg border border-white/[0.06] p-3">
                  <span>@{m.username}</span>
                  <div className="flex gap-2">
                    <Button
                      variant="primary"
                      data-testid={`club-member-approve-${m.id}`}
                      onClick={async () => {
                        await apiService.reviewClubMembership(m.id, 'approve');
                        await load();
                      }}
                    >
                      Approve
                    </Button>
                    <Button
                      variant="ghost"
                      data-testid={`club-member-deny-${m.id}`}
                      onClick={async () => {
                        await apiService.reviewClubMembership(m.id, 'reject');
                        await load();
                      }}
                    >
                      Deny
                    </Button>
                  </div>
                </li>
              ))}
          </ul>
        </section>

        <section>
          <h2 className="heading-section mb-3">Associate your vehicle</h2>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
            <OwnerVehicleCombobox
              selected={selectedVehicle}
              onSelect={(v) => {
                setSelectedVehicle(v);
                setLinkError(null);
              }}
              excludeIds={linkedVehicleIds}
              disabled={linkBusy}
            />
            <Button
              variant="secondary"
              disabled={!selectedVehicle || linkBusy}
              onClick={async () => {
                if (!selectedVehicle) return;
                setLinkBusy(true);
                setLinkError(null);
                try {
                  await apiService.linkClubVehicle(club.id, selectedVehicle.id, 'associated');
                  setSelectedVehicle(null);
                  await load();
                } catch (e: any) {
                  setLinkError(e.message || 'Failed to link vehicle');
                } finally {
                  setLinkBusy(false);
                }
              }}
            >
              {linkBusy ? 'Linking…' : 'Link'}
            </Button>
          </div>
          {linkError ? (
            <p role="alert" className="text-sm text-red-400 mt-2">
              {linkError}
            </p>
          ) : null}
          <p className="text-xs text-gray-500 mt-2">
            Search by year, make, model, or nickname. Only builds in your garage can be linked. Featured
            status requires admin action on an associated vehicle.
          </p>
        </section>

        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="heading-section">Club badges</h2>
            <Button variant="primary" onClick={() => setShowCreator((v) => !v)}>
              {showCreator ? 'Close creator' : 'Create badge'}
            </Button>
          </div>
          {showCreator ? (
            <BadgeCreator
              sourceType="club"
              sourceId={club.id}
              onSaved={async () => {
                setShowCreator(false);
                await load();
              }}
            />
          ) : null}
          <ul className="mt-4 space-y-2">
            {(data.badges || []).map((b: any) => (
              <li key={b.id} className="text-sm text-gray-300 border border-white/[0.06] rounded-lg px-3 py-2">
                {b.name} · {b.status} · {b.short_label}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
};

export default ClubManagePage;
